import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const URL = "https://coastsidelaw.com.au/home-hospital-wills-sunshine-coast";

const FAQS = [
  {
    q: "How much does a home or hospital will cost?",
    a: "A will prepared at your home, in hospital or in aged care is a fixed $880 including GST. There is no extra travel fee. If a medical report is needed to confirm capacity, the doctor's charge for that report is passed on at cost.",
  },
  {
    q: "Can you come to the hospital urgently?",
    a: `Yes. Urgent hospital and palliative care visits can be arranged, often the same or next day. Call ${FIRM.phone} and tell us how urgent it is.`,
  },
  {
    q: "Where do you visit?",
    a: "Homes, hospitals, aged care facilities and retirement villages across the Sunshine Coast, including Caloundra, Golden Beach, Pelican Waters, Baringa, Aura, Currimundi, Little Mountain and Sunshine Coast University Hospital.",
  },
  {
    q: "What if there are questions about capacity?",
    a: "The solicitor meets the will-maker privately and makes careful notes about their understanding. Where there is any doubt, we may ask the treating doctor for a medical report before the will is signed. This makes the will much harder to challenge later.",
  },
  {
    q: "Can a family member arrange the visit?",
    a: "Yes, family can make the booking. The solicitor will still speak with the will-maker alone, because the instructions must come from them, free of pressure.",
  },
  {
    q: "Can you do a power of attorney at the same visit?",
    a: "Yes. An enduring power of attorney and an advance health directive can be prepared and signed at the same visit.",
  },
];

export const Route = createFileRoute("/home-hospital-wills-sunshine-coast")({
  head: () => ({
    meta: [
      { title: fitTitle("Mobile Wills Sunshine Coast: Home & Hospital Visits | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "Mobile wills on the Sunshine Coast. A solicitor visits your home, hospital or aged care facility. Fixed $880 incl. GST, no travel fee. Urgent visits available.",
        ),
      },
      { property: "og:title", content: "Home, hospital and aged care wills on the Sunshine Coast" },
      { property: "og:description", content: fitDescription("A solicitor comes to you. Fixed $880 incl. GST, no travel fee, urgent visits available.") },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Mobile wills · Sunshine Coast"
      title="Home, hospital and aged care wills on the Sunshine Coast"
      lead="A Coastside Law solicitor will come to your home, hospital bed or aged care room to prepare and sign your will. The fee is a fixed $880 including GST, with no travel fee. Urgent visits can usually be arranged the same or next day."
      ctaLabel="Arrange a visit"
      boxTitle="What you get for $880"
      boxItems={[
        "A solicitor visits you anywhere on the Sunshine Coast",
        "No extra travel fee",
        "Private meeting with the will-maker and careful capacity notes",
        "Supervised signing, so the will is valid",
        "Urgent hospital and palliative care visits",
        "Medical report charges, if one is needed, passed on at cost",
      ]}
      faqs={FAQS}
      links={[
        { to: "/will-capacity-faqs", label: "Will capacity FAQs" },
        { to: "/wills-estate-lawyers-sunshine-coast", label: "Wills and estates Sunshine Coast" },
        { to: "/fixed-price-will-solicitor", label: "Fixed-price office wills" },
      ]}
      formHeading="Arrange a home or hospital will"
      ctaTitle="Need a will signed soon?"
      ctaBody={`Call ${FIRM.phone}. Tell us where and how urgent, and we'll come to you.`}
    />
  ),
});
